<template>
  <div ref="rootRef" class="l3">
    <a class="skip" href="#contenido">Saltar al contenido</a>
    <main id="contenido">
      <section class="hero wrap" aria-labelledby="titulo">
        <div class="hero-copy">
          <div class="eyebrow">Tu próximo cliente no espera al Word</div>
          <h1 v-if="isCopyV2" id="titulo">
            Sabés qué querés decir.<br />
            <span>No hace falta escribirlo desde cero.</span>
          </h1>
          <h1 v-else id="titulo">
            “Te mando la propuesta mañana.”<br />
            <span>Mañana ya<br />se enfrió.</span>
          </h1>
          <p v-if="isCopyV2">
            El trabajo lo sabés hacer. <strong>Prio genera documentos profesionales</strong>:
            propuestas, contratos, guías e informes con tu marca. Un primer borrador que revisás
            y editás antes de compartir.
          </p>
          <p v-else>
            El trabajo lo sabés hacer. Lo que te frena es ponerlo en papel.
            <strong>Prio genera documentos profesionales</strong> con tu marca, para que la hoja en
            blanco no se quede con el cliente.
          </p>
          <a class="cta" :href="SIGNUP_URL"
            >Probá gratis 30 días <span aria-hidden="true">↗</span>
          </a>
          <div class="guarantee">Sin tarjeta</div>
        </div>
        <div class="hero-visual">
          <div id="hero-scene" class="scene" :data-state="heroScene">
            <div class="orbit" aria-hidden="true"></div>
            <span class="spark" aria-hidden="true">✳</span>
            <div class="hero-sheet">
              <div class="blank" :aria-hidden="heroScene !== 'blank'">
                <div class="mono file">propuesta_final_v3.docx</div>
                <strong>Propuesta para…</strong>
                <span class="cursor" aria-hidden="true"> </span>
                <p class="blank-note">Trabado acá desde el martes.</p>
              </div>
              <div class="branded" :aria-hidden="heroScene === 'blank'">
                <div class="paper-top">
                  <span class="studio"> <i aria-hidden="true">✳</i> estudio sur</span>
                  <small class="mono">TU MARCA</small>
                </div>
                <h3>Una identidad<br />a la altura<br />de Casa Olivia.</h3>
                <p>
                  Juli, esta propuesta reúne el diseño de identidad visual para Casa Olivia:
                  logotipo y guía de uso, en 3 semanas.
                </p>
                <div class="doc-row">
                  <span>Inversión</span>
                  <b>$ 480.000</b>
                </div>
                <div class="doc-row">
                  <span>Entrega</span>
                  <b>3 semanas</b>
                </div>
              </div>
            </div>
            <div class="stamp" :aria-hidden="heroScene === 'blank'">{{ isCopyV2 ? '✦ Listo para revisar' : '✦ 10 minutos después' }}</div>
          </div>
          <div class="scene-controls" role="group" aria-label="Comparar antes y después">
            <button
              type="button"
              data-action="hero-scene"
              data-context="blank"
              :aria-pressed="heroScene === 'blank'"
              aria-controls="hero-scene"
              data-scene="blank"
              @click="setScene('blank')"
            >
              La hoja en blanco
            </button>
            <button
              type="button"
              data-action="hero-scene"
              data-context="ready"
              :aria-pressed="heroScene === 'ready'"
              aria-controls="hero-scene"
              data-scene="ready"
              @click="setScene('ready')"
            >
              Con Prio ↗
            </button>
          </div>
          <p class="caption mono">EJEMPLO ILUSTRATIVO · EL TIEMPO DEPENDE DEL DOCUMENTO</p>
        </div>
      </section>
      <section class="section pain wrap" aria-labelledby="dolor">
        <div class="pain-heading reveal">
          <div>
            <div class="eyebrow">No es solo un papel</div>
            <h2 id="dolor">
              Los papeles no cierran ventas.<br />
              <span class="accent">Pero te hacen ver<br />de otra liga.</span>
            </h2>
          </div>
          <p>
            Un choclo de texto deja lugar al “Yo pensé que incluía las redes”. Una propuesta clara
            pone el alcance sobre la mesa. Y tu trabajo donde merece.
          </p>
        </div>
        <div class="stats">
          <article class="stat reveal">
            <template v-if="isCopyV2">
              <strong aria-hidden="true">✦</strong>
              <p>
                <b>Documentos con tu marca.</b>
                <br />Propuestas, contratos, guías e informes.
              </p>
            </template>
            <template v-else>
              <strong aria-label="2 horas">
                <span data-count="2" aria-hidden="true">2</span>
                <small aria-hidden="true">hs</small>
              </strong>
              <p>
                <b>Un contrato desde cero.</b>
                <br />Y ese miedo a haberte olvidado algo.
              </p>
            </template>
          </article>
          <article class="stat reveal">
            <template v-if="isCopyV2">
              <strong aria-hidden="true">?</strong>
              <p>
                <b>Unas pocas preguntas.</b>
                <br />Sobre tu caso, no un formulario reciclado.
              </p>
            </template>
            <template v-else>
              <strong aria-label="3 a 5">
                <span data-count="3" aria-hidden="true">3</span>
                <span aria-hidden="true">–</span>
                <span data-count="5" aria-hidden="true">5</span>
              </strong>
              <p>
                <b>Preguntas de la IA sobre TU caso.</b>
                <br />No sobre un negocio que nada que ver.
              </p>
            </template>
          </article>
          <article class="stat reveal">
            <template v-if="isCopyV2">
              <strong aria-hidden="true">✓</strong>
              <p>
                <b>Vos revisás.</b>
                <br />Nombres, importes y plazos antes de compartir.
              </p>
            </template>
            <template v-else>
              <strong>0</strong>
              <p>
                <b>Hojas en blanco.</b>
                <br />Nunca más empezar mirando el cursor.
              </p>
            </template>
          </article>
        </div>
      </section>
      <section class="section flow" id="como" aria-labelledby="pasos">
        <div class="wrap">
          <div class="flow-head reveal">
            <div class="eyebrow">De “no sé cómo arrancar” a “revisalo”</div>
            <h2 id="pasos">
              Vos conocés el trabajo.<br />
              <span class="accent">Prio le da forma.</span>
            </h2>
            <p>
              No hace falta aprender a pedirle cosas a una IA. Elegís el papel, contás tu caso y
              respondés unas pocas preguntas. Después, lo dejás bien tuyo.
            </p>
          </div>
          <article class="step">
            <div class="step-copy reveal">
              <span class="step-number">01 / EL PAPEL CORRECTO</span>
              <h3>No todo se resuelve<br />con una propuesta.</h3>
              <p>
                Seis tipos, cada uno con su estructura. Para vender, acordar, dar la bienvenida,
                mostrar resultados o entender qué necesita el cliente.
              </p>
              <div v-if="isCopyV2" class="aside">
                ¿Ya tenés un Word que te costó horas? Elegí documento-base: pegás el contenido y
                Prio lo formaliza como base para revisarlo. Sin responder preguntas, porque el
                contenido ya está.
              </div>
              <div v-else class="aside">
                ¿Ya tenés un Word que te costó horas? Elegí documento-base: pegás el contenido y
                Prio lo formaliza sin perder nada. Sin preguntas, porque el contenido ya está.
              </div>
            </div>
            <div class="mock reveal">
              <div class="mock-top">
                <span class="mono">CREAR DOCUMENTO</span>
                <span class="badge">Solo elegís el tipo</span>
              </div>
              <h4>¿Qué necesitás poner en papel?</h4>
              <div class="types" role="group" aria-label="Tipos de documento">
                <button
                  v-for="t in docTypes"
                  :key="t.id"
                  type="button"
                  data-action="doc-type"
                  :data-context="t.id"
                  :aria-pressed="docType === t.id"
                  :data-type="t.id"
                  @click="setDocType(t.id)"
                >
                  <span aria-hidden="true">{{ t.icon }}</span
                  >{{ t.label }}
                </button>
              </div>
              <p id="type-note" class="type-note" role="status">{{ typeCopyShown[docType] }}</p>
              <div id="base-example" class="base-example" :hidden="docType !== 'base'">
                <b>Tu Word viejo → documento formal</b>
                <p>“logo + guía, 3 semanas, $ 480.000”</p>
                <b>Alcance y condiciones</b>
                <p>Diseño de logotipo y guía de uso. Plazo: 3 semanas. Inversión: $ 480.000.</p>
                <small>{{ isCopyV2 ? 'Ejemplo de formalización. Sin responder preguntas.' : 'Ejemplo de formalización. Sin Q&A.' }}</small>
              </div>
            </div>
          </article>
          <article class="step">
            <div class="step-copy reveal">
              <span class="step-number">02 / TU CASO, NO UNO PARECIDO</span>
              <h3>Contalo como<br />se lo contarías<br />a un colega.</h3>
              <p>
                Dos líneas alcanzan para arrancar. Eso que escribís es el ancla: las preguntas y el
                documento se arman sobre tu intención. No sobre una plantilla genérica.
              </p>
            </div>
            <div class="mock reveal">
              <div class="mock-top">
                <span class="mono">PROPUESTA COMERCIAL</span>
                <span class="badge">Tu punto de partida</span>
              </div>
              <h4>¿Qué querés proponer?</h4>
              <div class="intent">
                <p>
                  Quiero pasarle a Juli una propuesta de
                  <mark>identidad visual para Casa Olivia</mark>.<br />Necesita logo y guía de uso.
                  Las redes no están incluidas.
                </p>
              </div>
              <div class="anchor">
                <span aria-hidden="true">↳</span>Todo lo que sigue parte de esto.
              </div>
            </div>
          </article>
          <article class="step">
            <div class="step-copy reveal">
              <span class="step-number">03 / POCAS PREGUNTAS. BIEN HECHAS.</span>
              <h3>No te toma examen.<br />Te saca trabajo.</h3>
              <p v-if="isCopyV2">
                Respondés unas pocas preguntas, con opciones y espacio para tus datos reales. La IA
                arma algunas preguntas según tu caso, no un formulario reciclado.
              </p>
              <p v-else>
                Respondés unas pocas preguntas, con opciones y espacio para tus datos reales. La IA
                genera el 100% de las preguntas según tu caso: entre 3 y 5, no un formulario
                reciclado.
              </p>
              <p v-if="isCopyV2">
                Elegís el tono antes de generar: Semiformal por defecto, Formal o Corporativo. Y ya
                tenés un primer borrador.
              </p>
              <p v-else>
                Elegís el tono antes de generar: Semi formal por defecto, Formal o Corporativo. Y ya
                tenés un primer borrador.
              </p>
              <div v-if="isCopyV2" class="aside">
                Usa los datos que le das como base. Revisás nombres, importes y plazos antes de
                compartir.
              </div>
              <div v-else class="aside">
                Nombres, importes y plazos: lo que ponés se usa tal cual. La IA no inventa lo que no
                le contaste.
              </div>
            </div>
            <div class="mock reveal">
              <div class="mock-top">
                <span class="mono">PREGUNTAS PARA ESTE CASO</span>
                <span class="badge">Ejemplo interactivo</span>
              </div>
              <div class="question-label">¿Qué incluye la identidad de Casa Olivia?</div>
              <div id="scope-options" class="options" role="group" aria-label="Alcance del ejemplo">
                <button
                  v-for="opt in scopeOptions"
                  :key="opt"
                  type="button"
                  data-action="scope-option"
                  :data-context="opt"
                  :aria-pressed="scope === opt"
                  @click="setScope(opt)"
                >
                  {{ opt }}
                </button>
              </div>
              <div class="answer">
                <label for="amount">¿Cuál es la inversión? Escribí el dato tal cual.</label>
                <input
                  id="amount"
                  v-model="amount"
                  data-input="amount"
                  maxlength="45"
                  autocomplete="off"
                />
              </div>
              <div class="answer">
                <label for="deadline">¿En qué plazo lo entregás?</label>
                <input
                  id="deadline"
                  v-model="deadline"
                  data-input="deadline"
                  maxlength="45"
                  autocomplete="off"
                />
              </div>
              <div id="real-data" class="verbatim" aria-live="polite">{{ realData }}</div>
              <p class="caption">Demo local. No se envían datos ni se consulta una IA.</p>
            </div>
          </article>
          <article class="step">
            <div class="step-copy reveal">
              <span class="step-number">04 / TU CRITERIO, PÁRRAFO POR PÁRRAFO</span>
              <h3>Ese párrafo no te gusta.<br />Tocalo. No rehagas todo.</h3>
              <p>
                “Editar con IA” trabaja sobre el bloque que elegís, sin salir del documento. Más
                formal, más cercano. En las listas, agregá, reordená o hacé más conciso.
              </p>
              <p>
                Las versiones quedan guardadas. ¿La anterior decía mejor lo que querías? La
                restaurás. Probar no te obliga a perder lo que ya estaba bien.
              </p>
            </div>
            <div class="mock reveal">
              <div class="mock-top">
                <span class="mono">PROPUESTA / ALCANCE</span>
                <span id="current-version" class="badge">{{
                  'v' + (selected + 1) + (selected === current ? ' · actual' : ' · vista previa')
                }}</span>
              </div>
              <div class="edit-block">
                <p id="block-copy" ref="blockCopyRef" aria-live="polite">
                  {{ versions[selected] }}
                </p>
                <button
                  id="edit-toggle"
                  class="edit-button"
                  type="button"
                  data-action="block-edit-toggle"
                  :aria-expanded="editOpen"
                  aria-controls="edit-panel"
                  @click="toggleEdit"
                >
                  ✦ Editar con IA
                </button>
                <div id="edit-panel" class="edit-panel" :hidden="!editOpen">
                  <label>Pedile un cambio a este bloque:</label>
                  <div class="options">
                    <button
                      type="button"
                      data-action="block-rewrite"
                      data-context="formal"
                      data-rewrite="formal"
                      @click="rewriteBlock('formal')"
                    >
                      Hacelo más formal
                    </button>
                    <button
                      type="button"
                      data-action="block-rewrite"
                      data-context="cercano"
                      data-rewrite="cercano"
                      @click="rewriteBlock('cercano')"
                    >
                      Hacelo más cercano
                    </button>
                  </div>
                  <p class="caption">Respuestas de ejemplo, sin IA en vivo.</p>
                </div>
              </div>
              <div class="fixed-block">
                INVERSIÓN · $ 480.000<br />Este bloque no cambia al editar el alcance.
              </div>
              <div class="versions" role="group" aria-label="Versiones guardadas">
                <span class="mono">VERSIONES</span>
                <button
                  v-for="n in 3"
                  :key="n"
                  type="button"
                  data-action="block-version"
                  :data-context="'v' + n"
                  :data-version="String(n - 1)"
                  :aria-pressed="selected === n - 1"
                  :hidden="n - 1 >= versions.length"
                  @click="selectVersion(n - 1)"
                >
                  v{{ n }}
                </button>
                <button
                  id="restore"
                  class="restore"
                  type="button"
                  data-action="block-restore"
                  :disabled="selected === current"
                  @click="restoreVersion"
                >
                  Restaurar
                </button>
              </div>
              <p id="version-status" class="status" role="status">{{ versionStatus }}</p>
            </div>
          </article>
        </div>
      </section>
      <section class="tone-section wrap" id="tono" aria-labelledby="tono-titulo">
        <div class="tone-head reveal">
          <div class="eyebrow">Profesional no significa acartonado</div>
          <h2 id="tono-titulo">
            El mismo párrafo.<br />
            <span class="accent">La voz que pide el cliente.</span>
          </h2>
          <p>
            Juli no habla como un directorio. Tu documento tampoco tiene por qué. Cambiá el tono:
            los datos siguen siendo los mismos.
          </p>
        </div>
        <div class="tone-workbench reveal">
          <div class="tone-tabs" role="group" aria-label="Tono del mismo párrafo">
            <button
              type="button"
              data-action="tone-pick"
              data-context="semi"
              :aria-pressed="tone === 'semi'"
              aria-controls="tone-copy"
              data-tone="semi"
              @click="setTone('semi')"
            >
              {{ isCopyV2 ? 'Semiformal' : 'Semi formal' }}
            </button>
            <button
              type="button"
              data-action="tone-pick"
              data-context="formal"
              :aria-pressed="tone === 'formal'"
              aria-controls="tone-copy"
              data-tone="formal"
              @click="setTone('formal')"
            >
              Formal
            </button>
            <button
              type="button"
              data-action="tone-pick"
              data-context="corporativo"
              :aria-pressed="tone === 'corporativo'"
              aria-controls="tone-copy"
              data-tone="corporativo"
              @click="setTone('corporativo')"
            >
              Corporativo
            </button>
          </div>
          <div class="tone-paper">
            <div class="tone-meta mono">
              <span>ESTUDIO SUR → CASA OLIVIA</span>
              <span>01 / APERTURA</span>
            </div>
            <p
              id="tone-copy"
              ref="toneCopyRef"
              class="live-paragraph"
              aria-live="polite"
              aria-atomic="true"
            >
              {{ tones[tone][0] }}
            </p>
            <div class="tone-bottom">
              <span id="tone-label">{{ isCopyV2 && tone === 'semi' ? 'Semiformal · claro, sin distancia' : tones[tone][1] }}</span>
              <span>Mismos datos. Otra manera de decirlo.</span>
            </div>
          </div>
          <p class="caption">Demo de tonos con textos de ejemplo. Tocá para comparar.</p>
        </div>
        <p class="tone-note reveal">
          Después lo afinás: <b>“hacelo más cercano”</b> también funciona.
        </p>
      </section>
      <section class="section trust wrap" id="marca" aria-labelledby="confianza">
        <div class="trust-grid">
          <div class="trust-copy reveal">
            <div class="eyebrow">Bien escrito. Bien tuyo.</div>
            <h2 id="confianza">
              Lo que ves<br />es lo que sale<br />
              <span class="accent">en el PDF.</span>
            </h2>
            <p>
              Nada de “en el Word se veía distinto”. Cuatro estilos, tu logo y una presentación a la
              altura del trabajo que hay detrás.
            </p>
            <div class="trust-points">
              <div class="trust-point">
                <span aria-hidden="true">01</span>
                <div>
                  <b>Tu marca, no la de una plantilla.</b>
                  <p>Editorial, Minimal, Clásico o Moderno. Elegí el estilo y sumá tu logo.</p>
                </div>
              </div>
              <div class="trust-point">
                <span aria-hidden="true">02</span>
                <div>
                  <b>Una vista previa que cumple.</b>
                  <p v-if="isCopyV2">
                    Revisás el diseño antes de descargar el PDF. Lo que revisás es lo que
                    exportás.
                  </p>
                  <p v-else>
                    La vista previa y el PDF salen del mismo motor de renderizado. Lo que revisás es
                    lo que exportás.
                  </p>
                </div>
              </div>
              <div class="trust-point">
                <span aria-hidden="true">03</span>
                <div>
                  <b>{{ isCopyV2 ? 'Datos tuyos, con tu revisión.' : 'Datos tuyos. No inventos de la IA.' }}</b>
                  <p v-if="isCopyV2">
                    Usa los datos que le das como base. Revisás nombres, importes y plazos antes
                    de compartir.
                  </p>
                  <p v-else>
                    Usa tus datos reales tal cual. No inventa clientes ni resultados. Vos revisás el
                    borrador.
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div class="reveal">
            <div class="skin-workbench">
              <div class="skin-bar mono">
                <span>VISTA PREVIA</span>
                <span id="skin-label" role="status">{{
                  skin.toLocaleUpperCase('es-AR') + ' / 01'
                }}</span>
              </div>
              <article id="brand-doc" ref="brandDocRef" class="document" :data-skin="skin">
                <header class="paper-top">
                  <span class="studio">
                    <i v-if="showLogo" id="doc-logo" aria-label="Logo de ejemplo">✳</i> estudio
                    sur</span
                  >
                  <small class="mono">PROPUESTA</small>
                </header>
                <h3>Una identidad<br />a la altura<br />de Casa Olivia.</h3>
                <p>
                  Diseño de identidad visual: logotipo y guía de uso. Las redes sociales no están
                  incluidas.
                </p>
                <div class="doc-row">
                  <span>Inversión</span>
                  <b>$ 480.000</b>
                </div>
                <div class="doc-row">
                  <span>Plazo</span>
                  <b>3 semanas</b>
                </div>
                <footer class="mono">
                  <span>PREPARADO PARA JULI</span>
                  <span>01 / 01</span>
                </footer>
              </article>
              <div class="skin-picker" role="group" aria-label="Estilo del documento">
                <button
                  type="button"
                  data-action="doc-skin"
                  data-context="editorial"
                  :aria-pressed="skin === 'editorial'"
                  aria-controls="brand-doc"
                  data-skin="editorial"
                  @click="setSkin('editorial')"
                >
                  <span class="mini-paper" aria-hidden="true"> </span>Editorial
                </button>
                <button
                  type="button"
                  data-action="doc-skin"
                  data-context="minimal"
                  :aria-pressed="skin === 'minimal'"
                  aria-controls="brand-doc"
                  data-skin="minimal"
                  @click="setSkin('minimal')"
                >
                  <span class="mini-paper minimal" aria-hidden="true"> </span>Minimal
                </button>
                <button
                  type="button"
                  data-action="doc-skin"
                  data-context="clasico"
                  :aria-pressed="skin === 'clasico'"
                  aria-controls="brand-doc"
                  data-skin="clasico"
                  @click="setSkin('clasico')"
                >
                  <span class="mini-paper clasico" aria-hidden="true"> </span>Clásico
                </button>
                <button
                  type="button"
                  data-action="doc-skin"
                  data-context="moderno"
                  :aria-pressed="skin === 'moderno'"
                  aria-controls="brand-doc"
                  data-skin="moderno"
                  @click="setSkin('moderno')"
                >
                  <span class="mini-paper moderno" aria-hidden="true"> </span>Moderno
                </button>
              </div>
              <label class="logo-toggle">
                <input
                  id="show-logo"
                  v-model="showLogo"
                  type="checkbox"
                  data-input="show-logo"
                />Mostrar logo de ejemplo</label
              >
            </div>
            <p class="caption mono">CAMBIÁ EL ESTILO. EL CONTENIDO QUEDA INTACTO.</p>
          </div>
        </div>
        <p class="trust-note">
          Los documentos de esta página son ejemplos. Revisá datos, alcance y condiciones antes de
          compartir los tuyos.
        </p>
      </section>
      <section class="closing" aria-labelledby="accion">
        <div class="wrap reveal">
          <div class="eyebrow">Que el papel no te frene el trabajo</div>
          <h2 v-if="isCopyV2" id="accion">Tu próximo documento,<span>sin empezar desde cero.</span></h2>
          <h2 v-else id="accion">Que “te la mando mañana”<span>sea “te la mando ahora”.</span></h2>
          <p v-if="isCopyV2">Un borrador con tu marca para revisar. Antes, durante y después del trabajo.</p>
          <p v-else>Tu próximo documento no tiene por qué llevarse tu tarde.</p>
          <a class="cta" :href="SIGNUP_URL">Probá gratis <span aria-hidden="true">↗</span> </a>
          <div class="guarantee">Sin tarjeta · Cancelás cuando quieras</div>
        </div>
      </section>
    </main>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { useHead } from '@unhead/vue';
import { useScrollReveal } from '@/composables/useScrollReveal';
import { useCopyVariant } from '@/composables/useCopyVariant';
import { SIGNUP_URL } from '@/constants';

const { isCopyV2 } = useCopyVariant();

useHead({
  title: 'Prio - Documentos profesionales con tu marca',
  meta: [
    {
      name: 'description',
      content:
        'Propuestas, contratos, guías e informes con IA. Respondés unas pocas preguntas, sumás tu marca y queda listo para PDF.',
    },
    { name: 'theme-color', content: '#f7f3fb' },
  ],
  link: [
    { rel: 'preconnect', href: 'https://fonts.googleapis.com' },
    { rel: 'preconnect', href: 'https://fonts.gstatic.com', crossorigin: '' },
    {
      rel: 'stylesheet',
      href: 'https://fonts.googleapis.com/css2?family=Urbanist:wght@500;600;700;800;900&family=Inter:wght@400;500;600;700&family=IBM+Plex+Mono:wght@400;500&display=swap',
    },
  ],
});

const rootRef = ref<HTMLElement | null>(null);
const reducedMotion = ref(false);
let mediaQuery: MediaQueryList | null = null;
let heroTimer = 0;

function settle(el: HTMLElement | null): void {
  if (!reducedMotion.value && el && typeof el.animate === 'function') {
    el.animate(
      [
        { opacity: 0.35, transform: 'translateY(5px)' },
        { opacity: 1, transform: 'translateY(0)' },
      ],
      { duration: 300, easing: 'cubic-bezier(.2,.8,.2,1)' },
    );
  }
}

// Hero blank-page to branded-doc morph (illustrative demo).
const heroScene = ref<'blank' | 'ready'>('ready');

function setScene(state: 'blank' | 'ready'): void {
  window.clearTimeout(heroTimer);
  heroScene.value = state;
}

// Document types.
interface DocTypeOption {
  id: string;
  icon: string;
  label: string;
}

const docTypes: DocTypeOption[] = [
  { id: 'propuesta', icon: '↗', label: 'Propuesta comercial' },
  { id: 'contrato', icon: '§', label: 'Contrato' },
  { id: 'guia', icon: '☀', label: 'Guía de bienvenida' },
  { id: 'informe', icon: '↗', label: 'Informe de resultados' },
  { id: 'cuestionario', icon: '?', label: 'Cuestionario estratégico' },
  { id: 'base', icon: '≡', label: 'Documento-base' },
];
const docType = ref('propuesta');
const typeCopy: Record<string, string> = {
  propuesta: 'Tu alcance, tus tiempos y tu precio. El título también lo escribe la IA.',
  contrato: 'Lo acordado, por escrito. Una estructura para ordenar alcance y condiciones.',
  guia: 'El primer día, más claro: una guía para darle la bienvenida a tu cliente.',
  informe: 'Tus resultados, bien presentados. Con datos reales, no logros inventados.',
  cuestionario: 'Preguntas para entender qué necesita tu cliente antes de arrancar.',
  base: 'Pegá el contenido de tu Word. Prio lo formaliza sin perder nada y sin preguntas.',
};

// V2 softens the absolute: the pasted content is the base, review included.
const typeCopyShown = computed<Record<string, string>>(() =>
  isCopyV2.value
    ? {
        ...typeCopy,
        base: 'Pegá el contenido de tu Word. Prio lo formaliza como base para revisarlo.',
      }
    : typeCopy,
);

function setDocType(id: string): void {
  docType.value = id;
}

// Q&A real-data echo (local demo, nothing is sent).
const scopeOptions = ['Logo + guía de uso', 'Solo logotipo'];
const scope = ref('Logo + guía de uso');
const amount = ref('$ 480.000');
const deadline = ref('3 semanas');
const realData = computed<string>(
  () => 'En el documento: ' + scope.value + ' · ' + amount.value + ' · ' + deadline.value,
);

function setScope(option: string): void {
  scope.value = option;
}

// Per-block AI edit + versions (local demo).
const blockCopyRef = ref<HTMLElement | null>(null);
const versions = ref<string[]>([
  'Vamos a diseñar el logotipo y la guía de uso de Casa Olivia. Las redes sociales no están incluidas.',
]);
const rewrites: Record<string, string> = {
  formal:
    'El alcance comprende el diseño del logotipo y la guía de uso de Casa Olivia. No incluye redes sociales.',
  cercano:
    'Vamos a darle forma a la identidad de Casa Olivia con su logotipo y guía de uso. Las redes sociales quedan fuera de esta propuesta.',
};
const current = ref(0);
const selected = ref(0);
const editOpen = ref(false);
const versionStatus = ref('Tocá “Editar con IA” y probá un cambio.');

function toggleEdit(): void {
  editOpen.value = !editOpen.value;
}

function showSelected(): void {
  settle(blockCopyRef.value);
}

function rewriteBlock(kind: string): void {
  const text = rewrites[kind];
  let index = versions.value.indexOf(text);
  if (index === -1) {
    versions.value.push(text);
    index = versions.value.length - 1;
  }
  current.value = index;
  selected.value = index;
  versionStatus.value =
    'v' + (current.value + 1) + ' guardada en esta demo. Elegí otra versión para restaurarla.';
  showSelected();
}

function selectVersion(index: number): void {
  selected.value = index;
  versionStatus.value =
    selected.value === current.value
      ? 'Esta es la versión actual.'
      : 'Estás viendo v' + (selected.value + 1) + '. Tocá Restaurar para volver a ella.';
  showSelected();
}

function restoreVersion(): void {
  current.value = selected.value;
  versionStatus.value =
    'v' + (current.value + 1) + ' restaurada. Las otras versiones siguen guardadas en esta demo.';
  showSelected();
}

// Tone picker: same paragraph, three voices (example texts).
const toneCopyRef = ref<HTMLElement | null>(null);
const tones: Record<string, [string, string]> = {
  semi: [
    'Juli, esta propuesta reúne el diseño de identidad visual para Casa Olivia: logotipo y guía de uso, en 3 semanas, por $ 480.000.',
    'Semi formal · claro, sin distancia',
  ],
  formal: [
    'Juli, presentamos la propuesta de diseño de identidad visual para Casa Olivia. Comprende el logotipo y la guía de uso, con un plazo de 3 semanas y una inversión de $ 480.000.',
    'Formal · preciso, sin vueltas',
  ],
  corporativo: [
    'Juli, la presente propuesta contempla el desarrollo de identidad visual para Casa Olivia. El alcance incluye logotipo y guía de uso, con un plazo de ejecución de 3 semanas y una inversión de $ 480.000.',
    'Corporativo · institucional, sin cambiar los datos',
  ],
};
const tone = ref('semi');

function setTone(next: string): void {
  tone.value = next;
  settle(toneCopyRef.value);
}

// Skins + logo (local demo).
const brandDocRef = ref<HTMLElement | null>(null);
const skin = ref('editorial');
const showLogo = ref(true);

function setSkin(next: string): void {
  skin.value = next;
  settle(brandDocRef.value);
}

// Stat count-ups (client-only, honors reduced motion).
let countObserver: IntersectionObserver | null = null;
let disposed = false;
let countsRafId = 0;

function animateCounts(container: HTMLElement): void {
  const elements = Array.from(container.querySelectorAll('[data-count]'));
  if (!elements.length || reducedMotion.value) return;
  const start = performance.now();
  const tick = (now: number): void => {
    if (disposed) return;
    const progress = Math.min(1, (now - start) / 650);
    elements.forEach((node) => {
      if (node instanceof HTMLElement) {
        node.textContent = String(
          Math.round(Number(node.dataset.count) * (1 - Math.pow(1 - progress, 3))),
        );
      }
    });
    if (progress >= 1 || reducedMotion.value) {
      elements.forEach((node) => {
        if (node instanceof HTMLElement) node.textContent = String(Number(node.dataset.count));
      });
      return;
    }
    countsRafId = requestAnimationFrame(tick);
  };
  countsRafId = requestAnimationFrame(tick);
}

function onMotionChange(): void {
  reducedMotion.value = mediaQuery?.matches ?? false;
  if (reducedMotion.value) {
    window.clearTimeout(heroTimer);
    setScene('ready');
  }
}

onMounted(() => {
  mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  reducedMotion.value = mediaQuery.matches;
  mediaQuery.addEventListener('change', onMotionChange);
  if (!reducedMotion.value) {
    setScene('blank');
    heroTimer = window.setTimeout(() => setScene('ready'), 1800);
  }
  if (!('IntersectionObserver' in window)) return;
  countObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        countObserver?.unobserve(entry.target);
        if (entry.target instanceof HTMLElement) animateCounts(entry.target);
      });
    },
    { threshold: 0.5 },
  );
  rootRef.value?.querySelectorAll('.stat').forEach((el) => countObserver?.observe(el));
});

onUnmounted(() => {
  disposed = true;
  window.clearTimeout(heroTimer);
  countObserver?.disconnect();
  mediaQuery?.removeEventListener('change', onMotionChange);
  cancelAnimationFrame(countsRafId);
});

useScrollReveal();
</script>

<style scoped>
/* Prototype delta: slightly warmer muted than the canonical token. */
.l3 {
  --muted: #70657d;
}
h1,
h2,
h3 {
  font-family: Urbanist, Arial, sans-serif;
  letter-spacing: -0.045em;
  line-height: 1.04;
  font-weight: 800;
}
h1 {
  font-size: clamp(49px, 6vw, 79px);
}
h2 {
  font-size: clamp(39px, 4.5vw, 61px);
}
h3 {
  font-size: 34px;
}

a {
  color: inherit;
  text-decoration: none;
}
button,
input,
textarea {
  font: inherit;
  color: inherit;
}
button {
  cursor: pointer;
}
button,
a {
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
}
button {
  border: 1px solid var(--line);
  background: #fffcff;
  border-radius: 12px;
  min-height: 44px;
  padding: 9px 14px;
}
a {
  display: inline-flex;
  align-items: center;
  min-height: 44px;
}
button:hover {
  border-color: #9b7dc4;
}
button:active,
.cta:active {
  transform: scale(0.98);
}

button[aria-pressed='true'] {
  background: var(--ink);
  color: #fff;
  border-color: var(--ink);
}
button:disabled {
  cursor: default;
  opacity: 0.5;
}
textarea {
  resize: vertical;
  width: 100%;
  border: 1px solid #cbbadd;
  background: #fff;
  border-radius: 12px;
  padding: 16px;
  font-size: 16px;
  line-height: 1.6;
}
input {
  min-height: 44px;
  width: 100%;
  background: #fff;
  border: 1px solid var(--line);
  border-radius: 9px;
  padding: 9px 12px;
  font-size: 16px;
}
label {
  display: block;
  font-size: 12px;
  margin-bottom: 8px;
}
small {
  font-size: 11px;
}
::selection {
  background: var(--lime);
  color: var(--ink);
}
[hidden] {
  display: none !important;
}
.wrap {
  width: min(1160px, calc(100% - 40px));
  margin: auto;
}
.mono,
.eyebrow {
  font-family: 'IBM Plex Mono', monospace;
  font-size: 10px;
  letter-spacing: 0.065em;
}
.eyebrow {
  text-transform: uppercase;
  color: var(--violet);
  margin-bottom: 22px;
}
.eyebrow:before {
  content: ' ';
  display: inline-block;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--pink);
  margin-right: 9px;
  vertical-align: middle;
}
.crumb {
  color: var(--muted);
  font-size: 11px;
  border-left: 1px solid var(--line);
  padding-left: 20px;
  margin-left: 20px;
}

.hero {
  padding: 68px 0 75px;
  display: grid;
  grid-template-columns: 1.05fr 1fr;
  gap: 55px;
  align-items: center;
}
.hero h1 span {
  color: var(--violet);
}
.hero-copy > p {
  max-width: 420px;
  margin: 26px 0;
  font-size: 15px;
}
.cta {
  min-height: 56px;
  display: inline-flex;
  justify-content: space-between;
  gap: 32px;
  padding: 14px 25px;
  background: var(--aurora);
  border: 1px solid #c8acdc;
  border-radius: 16px;
  font-size: 13px;
  font-weight: 700;
  position: relative;
  overflow: hidden;
  isolation: isolate;
  transition: transform 0.2s var(--ease);
}
.cta:before {
  content: '';
  position: absolute;
  inset: -100%;
  background: linear-gradient(110deg, transparent 45%, #ffffff85 50%, transparent 55%);
  transform: translateX(-70%);
  z-index: -1;
}
.cta:hover:before {
  animation: sheen 0.8s var(--ease);
}
.guarantee {
  font-size: 11px;
  margin-top: 10px;
  color: var(--muted);
}
.hero-visual {
  min-width: 0;
}
.scene {
  height: 470px;
  position: relative;
  isolation: isolate;
}
.scene:before {
  content: '';
  position: absolute;
  inset: 40px -8px 25px;
  background: var(--aurora);
  border-radius: 49% 51% 41% 59% / 49% 38% 62% 51%;
  transform: rotate(-9deg);
  opacity: 0.6;
  z-index: -2;
}
.orbit {
  position: absolute;
  inset: 10px 15px;
  border: 1px solid #bba5d1;
  border-radius: 50%;
  transform: rotate(24deg);
  z-index: -1;
}
.spark {
  position: absolute;
  right: 0;
  top: 0;
  font-size: 67px;
  line-height: 1;
  color: var(--violet);
  transform: rotate(12deg);
}
.hero-sheet {
  position: absolute;
  inset: 44px 30px 38px;
  transform: rotate(-4deg);
  background: #fffdfa;
  border: 1px solid #ece1ec;
  box-shadow: 0 24px 55px #42305518;
  border-radius: 5px;
  transition: transform 0.85s var(--ease);
}
.hero-sheet:before {
  content: '';
  position: absolute;
  inset: 7px -8px -8px 7px;
  border: 1px solid #e5d7e5;
  background: #fffbff;
  z-index: -1;
  transform: rotate(3deg);
  border-radius: 5px;
}
.blank,
.branded {
  position: absolute;
  inset: 0;
  padding: 30px;
  transition:
    opacity 0.6s var(--ease),
    transform 0.7s var(--ease);
}
.blank {
  opacity: 0;
  transform: translateY(-8px);
  pointer-events: none;
}
.blank .file {
  font-size: 10px;
  letter-spacing: -0.025em;
  border-bottom: 1px solid var(--line);
  padding-bottom: 14px;
  overflow-wrap: anywhere;
}
.blank strong {
  display: block;
  margin-top: 44px;
  font: 500 36px Urbanist;
  color: #998ba6;
}
.cursor {
  display: block;
  width: 2px;
  height: 30px;
  background: var(--violet);
  margin-top: 20px;
}
.blank-note {
  margin-top: 65px;
  font-size: 12px;
}
.branded h3 {
  font-size: 37px;
  margin: 28px 0 15px;
}
.branded p {
  font-size: 11px;
  line-height: 1.8;
}
.studio {
  font: 800 22px Urbanist;
  letter-spacing: -1px;
  display: flex;
  align-items: center;
  gap: 9px;
}
.studio i {
  font-style: normal;
  color: var(--violet);
  font-size: 28px;
}
.paper-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #d8cbdc;
  padding-bottom: 13px;
  gap: 12px;
}
.paper-top small {
  font-size: 8px;
}
.doc-row {
  display: flex;
  justify-content: space-between;
  gap: 15px;
  border-bottom: 1px solid var(--line);
  font-size: 11px;
  padding: 12px 0;
}
.stamp {
  position: absolute;
  right: 0;
  bottom: 10px;
  background: var(--lime);
  border: 1px solid #becf8e;
  border-radius: 13px;
  padding: 12px 18px;
  transform: rotate(-4deg);
  box-shadow: 0 8px 20px #231b3610;
  font-size: 13px;
  transition:
    opacity 0.5s,
    transform 0.5s;
}
.scene[data-state='blank'] .hero-sheet {
  transform: rotate(3deg);
}
.scene[data-state='blank'] .blank {
  opacity: 1;
  transform: none;
}
.scene[data-state='blank'] .branded {
  opacity: 0;
  transform: translateY(14px);
}
.scene[data-state='blank'] .stamp {
  opacity: 0;
  transform: translateY(10px) rotate(-4deg);
}
.scene-controls {
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-top: 13px;
}
.scene-controls button {
  font-size: 11px;
  border-radius: 30px;
}
.caption {
  text-align: center;
  font-size: 10px;
  color: var(--muted);
  margin-top: 12px;
}
.section {
  padding: 90px 0;
}
.pain {
  border-top: 1px solid var(--line);
}
.pain-heading {
  display: flex;
  justify-content: space-between;
  gap: 60px;
  align-items: end;
}
.pain-heading h2 {
  max-width: 670px;
}
.pain-heading p {
  max-width: 275px;
}

.stats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 35px;
  margin-top: 48px;
}
.stat {
  border-top: 1px solid #bba8cf;
  padding-top: 20px;
}
.stat strong {
  display: block;
  font: 700 clamp(78px, 9vw, 118px) / 1 Urbanist;
  letter-spacing: -0.08em;
}
.stat strong small {
  font-size: 33px;
  letter-spacing: -0.05em;
  margin-left: 5px;
}
.stat p {
  font-size: 13px;
  max-width: 260px;
  margin-top: 18px;
}
.stat b {
  color: var(--ink);
  font-weight: 600;
}
.flow {
  background: #f0eaf6;
  border-top: 1px solid var(--line);
  border-bottom: 1px solid var(--line);
}
.flow-head {
  max-width: 710px;
  margin-bottom: 65px;
}
.flow-head > p {
  margin-top: 22px;
  max-width: 530px;
}
.step {
  display: grid;
  grid-template-columns: 1fr 1.12fr;
  gap: 85px;
  align-items: center;
  padding: 42px 0 58px;
}
.step + .step {
  border-top: 1px solid #d9cde4;
}
.step-copy {
  max-width: 390px;
}
.step-number {
  display: block;
  font:
    500 12px 'IBM Plex Mono',
    monospace;
  color: var(--violet);
  margin-bottom: 23px;
}
.step-copy p {
  margin-top: 20px;
}
.aside {
  font-size: 12px;
  border-left: 2px solid #b59bd5;
  padding-left: 16px;
  margin-top: 22px;
  color: #62516f;
}
.mock {
  min-width: 0;
  background: #fffaff;
  border: 1px solid #fff;
  border-radius: 24px;
  padding: 25px;
  box-shadow: 0 16px 40px #31204709;
}
.mock-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  border-bottom: 1px solid var(--line);
  padding-bottom: 16px;
  margin-bottom: 20px;
  font-size: 10px;
  color: var(--muted);
}
.badge {
  padding: 4px 9px;
  background: #ede5f9;
  border-radius: 30px;
  color: var(--violet);
  font-size: 9px;
  white-space: nowrap;
}
.mock h4 {
  font: 800 24px Urbanist;
  letter-spacing: -0.04em;
  margin: 0 0 18px;
}
.types {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}
.types button {
  text-align: left;
  min-height: 76px;
  font-size: 12px;
  padding: 13px;
  display: flex;
  align-items: center;
  gap: 10px;
}
.types button span {
  font: 500 17px 'IBM Plex Mono';
  opacity: 0.65;
}
.type-note {
  font-size: 12px;
  padding-top: 15px;
  min-height: 60px;
}
.base-example {
  margin-top: 12px;
  padding: 14px;
  border-radius: 10px;
  background: #f1ebf7;
  font-size: 12px;
}
.base-example p {
  margin: 5px 0 10px;
}
.intent {
  background: #f8f4fc;
  border: 1px solid #c8b5e1;
  border-radius: 13px;
  padding: 19px;
  font-size: 15px;
  line-height: 1.8;
}
.intent p {
  color: var(--ink);
}
.intent mark {
  background: #e4d8f5;
  color: inherit;
  padding: 2px 3px;
}
.anchor {
  display: flex;
  gap: 12px;
  align-items: center;
  margin-top: 18px;
  font-size: 11px;
  color: var(--violet);
}
.anchor span {
  font-size: 24px;
}
.question-label {
  font-weight: 600;
  font-size: 13px;
  margin: 15px 0 10px;
}
.options {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}
.options button {
  font-size: 11px;
  flex: 1;
  padding: 8px;
}
.answer {
  padding: 12px 0 0;
}
.answer label {
  color: var(--muted);
}
.verbatim {
  margin-top: 13px;
  padding: 12px;
  background: #f0f6e6;
  color: #40532b;
  font-size: 11px;
  border-radius: 9px;
}
.edit-block {
  border: 1px dashed #a382cb;
  border-radius: 12px;
  padding: 17px;
  background: #f6f0fd;
}
.edit-block p {
  color: var(--ink);
  font-size: 13px;
  min-height: 70px;
}
.edit-button {
  margin-top: 13px;
  color: var(--violet);
  font-size: 11px;
  background: #fff;
}
.edit-panel {
  padding-top: 14px;
}
.edit-panel .options button {
  flex: auto;
}
.versions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 18px;
}
.versions button {
  font-size: 11px;
  min-width: 44px;
  padding: 8px 11px;
}
.versions .restore {
  margin-left: auto;
  color: var(--violet);
}
.status {
  font-size: 11px;
  min-height: 39px;
  margin-top: 10px;
  color: var(--violet);
}
.fixed-block {
  padding-top: 18px;
  font-size: 11px;
  color: var(--muted);
}
.tone-section {
  padding: 110px 0;
}
.tone-head {
  text-align: center;
  max-width: 780px;
  margin: auto;
}
.tone-head p {
  margin: 22px auto 0;
  max-width: 520px;
}
.tone-workbench {
  margin: 42px auto 0;
  max-width: 980px;
  border: 1px solid #d2bfdf;
  background: var(--aurora);
  padding: 30px;
  border-radius: 30px;
  position: relative;
  isolation: isolate;
}
.tone-tabs {
  display: flex;
  gap: 7px;
  justify-content: center;
  margin-bottom: 25px;
}
.tone-tabs button {
  border-radius: 30px;
  font-size: 12px;
  min-width: 120px;
  background: #ffffff8c;
}
.tone-tabs button[aria-pressed='true'] {
  background: var(--ink);
}
.tone-paper {
  background: #fffdfb;
  border: 1px solid white;
  border-radius: 5px;
  padding: 36px 45px;
  box-shadow: 0 16px 40px #3b244512;
  position: relative;
}
.tone-paper:before {
  content: '“';
  font: 100px Georgia;
  color: #b69ccd;
  position: absolute;
  top: 55px;
  left: 19px;
  opacity: 0.35;
}
.tone-meta {
  display: flex;
  justify-content: space-between;
  gap: 15px;
  font-size: 9px;
  color: var(--muted);
  margin-bottom: 28px;
}
.live-paragraph {
  font: 600 clamp(23px, 2.6vw, 33px) / 1.48 Urbanist;
  letter-spacing: -0.025em;
  min-height: 148px;
  position: relative;
  color: var(--ink);
}
.tone-bottom {
  margin-top: 28px;
  border-top: 1px solid var(--line);
  padding-top: 15px;
  display: flex;
  justify-content: space-between;
  font-size: 10px;
  gap: 15px;
  color: var(--muted);
}
.tone-note {
  text-align: center;
  margin-top: 22px;
  font-size: 13px;
}
.tone-note b {
  font-weight: 500;
  color: var(--ink);
}
.trust {
  padding-top: 20px;
}
.trust-grid {
  display: grid;
  grid-template-columns: 0.9fr 1.1fr;
  gap: 75px;
  align-items: center;
}
.trust-copy > p {
  margin-top: 24px;
}
.trust-points {
  margin-top: 30px;
}
.trust-point {
  border-top: 1px solid var(--line);
  padding: 18px 0;
  display: flex;
  gap: 15px;
  font-size: 13px;
}
.trust-point span {
  color: var(--violet);
}
.trust-point b {
  display: block;
  margin-bottom: 3px;
  font-weight: 600;
}
.trust-point p {
  font-size: 12px;
}
.skin-workbench {
  background: #eae2f2;
  border: 1px solid #d6c8e2;
  border-radius: 25px;
  padding: 24px;
}
.skin-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 18px;
  font-size: 10px;
  color: var(--muted);
}
.document {
  --doc-accent: #614077;
  background: #fffdfa;
  padding: 30px;
  border: 1px solid #ded5df;
  box-shadow: 0 14px 32px #3120470e;
  min-height: 365px;
  color: var(--ink);
}
.document .studio {
  color: var(--doc-accent);
}
.document h3 {
  margin: 25px 0 15px;
  font-size: 35px;
  color: var(--doc-accent);
}
.document p {
  font-size: 12px;
}
.document footer {
  display: flex;
  justify-content: space-between;
  border-top: 1px solid #d8ccdf;
  margin-top: 25px;
  padding-top: 13px;
  font-size: 8px;
  color: var(--doc-accent);
}
.document[data-skin='editorial'] h3 {
  font-family: Georgia, serif;
  font-weight: 400;
  font-size: 36px;
  letter-spacing: -0.055em;
}
.document[data-skin='minimal'] {
  --doc-accent: #35333b;
  background: #fff;
}
.document[data-skin='minimal'] .paper-top {
  border: 0;
}
.document[data-skin='minimal'] h3 {
  font-weight: 500;
  letter-spacing: -0.03em;
}
.document[data-skin='clasico'] {
  --doc-accent: #674e33;
  background: #fffcf4;
  outline: 1px solid #baa789;
  outline-offset: -10px;
}
.document[data-skin='clasico'] h3 {
  font-family: Georgia, serif;
  letter-spacing: -0.04em;
  font-size: 32px;
  text-align: center;
}
.document[data-skin='clasico'] .paper-top {
  border-bottom: 3px double #baa789;
}
.document[data-skin='moderno'] {
  --doc-accent: #6541bd;
  border-top: 9px solid #6541bd;
  background: linear-gradient(140deg, #fff 60%, #f0e5ff);
}
.document[data-skin='moderno'] h3 {
  font-weight: 900;
}
.skin-picker {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
  margin-top: 20px;
}
.skin-picker button {
  background: transparent;
  padding: 8px 4px;
  border-color: transparent;
  font-size: 10px;
  border-radius: 12px;
}
.skin-picker button[aria-pressed='true'] {
  background: #fff;
  color: var(--violet);
  border-color: #b99acf;
}
.mini-paper {
  display: block;
  width: 41px;
  height: 51px;
  margin: 0 auto 8px;
  background: #fffdfb;
  box-shadow: 0 2px 7px #231b3614;
  padding: 9px 6px;
  text-align: left;
  border: 1px solid #ddd1e4;
}
.mini-paper:before {
  content: 'Aa';
  font: 14px Georgia;
  display: block;
  line-height: 1;
}
.mini-paper:after {
  content: '';
  display: block;
  height: 10px;
  margin-top: 5px;
  background: repeating-linear-gradient(to bottom, #c9bed4 0 1px, transparent 1px 4px);
}
.minimal:before {
  font-family: Arial;
}
.clasico {
  outline: 1px solid #bca58b;
  outline-offset: -4px;
  background: #fffcf4;
}
.moderno {
  border-top: 5px solid var(--violet);
  background: #f2e9ff;
}
.moderno:before {
  font: 800 14px Urbanist;
}
.logo-toggle {
  display: flex;
  align-items: center;
  gap: 9px;
  margin-top: 12px;
  font-size: 11px;
  min-height: 44px;
  width: max-content;
  max-width: 100%;
  cursor: pointer;
}
.logo-toggle input {
  width: 18px;
  min-height: 18px;
  height: 18px;
  accent-color: var(--violet);
}
.trust-note {
  margin-top: 26px;
  text-align: center;
  font-size: 11px;
}
.closing {
  position: relative;
  text-align: center;
  background: var(--aurora);
  padding: 85px 20px 90px;
  overflow: hidden;
  margin-top: 70px;
  border-top: 1px solid #d4bddf;
}
.closing:before {
  content: '';
  position: absolute;
  width: 800px;
  height: 800px;
  left: 50%;
  top: 30px;
  transform: translateX(-50%);
  border: 1px solid #fff8;
  border-radius: 50%;
  pointer-events: none;
}
.closing .wrap {
  position: relative;
}
.closing h2 {
  max-width: 780px;
  margin: auto;
}
.closing h2 span {
  display: block;
  color: #65418c;
}
.closing p {
  margin: 25px 0;
  color: #65516f;
}
.closing .cta {
  background: #fff9fd;
  border-color: #cfadd6;
  min-width: 200px;
}
.closing .eyebrow {
  color: #624276;
}
.motion .reveal {
  opacity: 0;
  transform: translateY(22px);
  transition:
    opacity 0.65s var(--ease),
    transform 0.65s var(--ease);
}

@keyframes sheen {
  to {
    transform: translateX(70%);
  }
}
@media (min-width: 1400px) {
  .hero {
    padding-top: 90px;
    padding-bottom: 95px;
  }
}
@media (max-width: 850px) {
  .hero {
    gap: 25px;
  }
  .hero h1 {
    font-size: 55px;
  }
  .hero-sheet {
    inset: 50px 14px 45px;
  }
  .blank,
  .branded {
    padding: 23px;
  }
  .branded h3 {
    font-size: 30px;
  }
  .scene {
    height: 430px;
  }
  .step {
    gap: 35px;
  }
  .trust-grid {
    gap: 35px;
  }
  .tone-paper {
    padding: 30px;
  }
  .pain-heading {
    gap: 30px;
  }
  .pain-heading h2 {
    font-size: 44px;
  }
}
@media (max-width: 640px) {
  .crumb {
    font-size: 9px;
    padding-left: 12px;
    margin-left: 12px;
  }
  .hero {
    grid-template-columns: 1fr;
    padding: 35px 0 50px;
    gap: 26px;
  }
  .hero h1 {
    font-size: clamp(47px, 12.8vw, 66px);
  }
  .hero-copy > p {
    font-size: 13px;
    margin: 24px 0;
  }
  .eyebrow {
    font-size: 9px;
    letter-spacing: 0.04em;
    margin-bottom: 19px;
  }
  .scene {
    height: 397px;
    max-width: 410px;
    margin: auto;
  }
  .hero-sheet {
    inset: 32px 24px 35px;
  }
  .branded,
  .blank {
    padding: 24px;
  }
  .branded h3 {
    font-size: 31px;
    margin-top: 21px;
  }
  .branded p {
    font-size: 10px;
  }
  .studio {
    font-size: 20px;
  }
  .scene:before {
    inset: 32px 0 16px;
  }
  .spark {
    font-size: 53px;
    right: 4px;
  }
  .stamp {
    font-size: 11px;
    padding: 10px 14px;
    bottom: 9px;
  }
  .blank-note {
    margin-top: 55px;
  }
  .section {
    padding: 62px 0;
  }
  .pain-heading {
    display: block;
  }
  .pain-heading h2 {
    font-size: 39px;
  }
  .pain-heading > p {
    margin-top: 22px;
    max-width: none;
    font-size: 13px;
  }
  .stats {
    gap: 16px;
    margin-top: 35px;
    grid-template-columns: 1fr;
  }
  .stat {
    display: grid;
    grid-template-columns: 110px 1fr;
    gap: 22px;
    padding: 19px 0 8px;
    align-items: center;
  }
  .stat strong {
    font-size: 75px;
  }
  .stat strong small {
    font-size: 23px;
  }
  .stat p {
    font-size: 12px;
    margin: 0;
  }
  .stat:nth-child(2) strong {
    font-size: 58px;
    letter-spacing: -0.07em;
  }
  .flow-head {
    margin-bottom: 15px;
  }
  .flow-head > p {
    font-size: 13px;
  }
  .step {
    grid-template-columns: 1fr;
    gap: 25px;
    padding: 32px 0 38px;
  }
  .step-number {
    margin-bottom: 16px;
  }
  .step-copy {
    max-width: none;
  }
  .step-copy h3 {
    font-size: 33px;
  }
  .step-copy p {
    font-size: 13px;
    margin-top: 16px;
  }
  .mock {
    padding: 20px;
    border-radius: 20px;
  }
  .types button {
    font-size: 11px;
    gap: 8px;
  }
  .aside {
    font-size: 11px;
  }
  .tone-section {
    padding: 65px 0;
  }
  .tone-head {
    text-align: left;
  }
  .tone-head p {
    font-size: 13px;
  }
  .tone-workbench {
    padding: 15px;
    border-radius: 22px;
    margin-top: 28px;
  }
  .tone-tabs {
    gap: 4px;
    margin-bottom: 15px;
  }
  .tone-tabs button {
    min-width: 0;
    flex: 1;
    font-size: 11px;
    padding: 9px 7px;
  }
  .tone-paper {
    padding: 22px 19px;
  }
  .tone-meta {
    font-size: 8px;
    margin-bottom: 20px;
  }
  .live-paragraph {
    font-size: 25px;
    min-height: 260px;
  }
  .tone-paper:before {
    left: 5px;
    top: 52px;
    font-size: 75px;
  }
  .tone-bottom {
    font-size: 9px;
    flex-wrap: wrap;
    gap: 4px;
  }
  .tone-note {
    font-size: 12px;
  }
  .trust {
    padding-top: 5px;
  }
  .trust-grid {
    grid-template-columns: 1fr;
    gap: 26px;
  }
  .trust-copy > p {
    font-size: 13px;
  }
  .skin-workbench {
    padding: 17px;
  }
  .document {
    padding: 25px 22px;
    min-height: 354px;
  }
  .document h3 {
    font-size: 32px;
  }
  .document[data-skin='editorial'] h3 {
    font-size: 33px;
  }
  .skin-picker {
    gap: 3px;
  }
  .skin-picker button {
    font-size: 9px;
  }
  .closing {
    padding: 64px 0;
    margin-top: 20px;
  }
  .closing .wrap {
    width: calc(100% - 40px);
  }
  .closing h2 {
    font-size: 42px;
  }
  .closing p {
    font-size: 13px;
  }
  .caption {
    font-size: 9px;
  }
}
@media (prefers-reduced-motion: reduce) {
  html {
    scroll-behavior: auto;
  }
  *,
  *:before,
  *:after {
    animation: none !important;
    transition: none !important;
  }
  .motion .reveal {
    opacity: 1;
    transform: none;
  }
}
</style>
